/**
 * SidebarContext - Global state management for sidebar collapse state
 *
 * Features:
 * - Persists sidebar preference to localStorage
 * - Provides consistent sidebar state across page navigations
 * - Defaults to expanded on desktop viewports
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'sidebar-expanded';

interface SidebarContextType {
  isExpanded: boolean;
  toggle: () => void;
  setExpanded: (expanded: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | null>(null);

interface SidebarProviderProps {
  children: React.ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  // Initialize with true (expanded) as default for desktop
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    // Check localStorage for persisted preference
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      // If user has explicitly set a preference, use it
      // Otherwise default to expanded (true)
      if (stored !== null) {
        return stored === 'true';
      }
    }
    // Default to expanded for desktop
    return true;
  });

  // Persist to localStorage when changed
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, String(isExpanded));
    }
  }, [isExpanded]);

  const toggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const setExpanded = useCallback((expanded: boolean) => {
    setIsExpanded(expanded);
  }, []);

  return (
    <SidebarContext.Provider value={{ isExpanded, toggle, setExpanded }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export default SidebarContext;
